import { AnimationAction, AnimationClip, AnimationMixer, SkinnedMesh } from "three";
import { Ticker } from "pixi.js";
import { AnimationParsingUtils } from "../../../../../three/AnimationParsingUtils";
import { Turret } from "./Turret";

export class TurretAnimationController {
    private readonly animationMixer: AnimationMixer;
    private spawn: AnimationAction;
    private hide: AnimationAction;
    private shoot: AnimationAction;

    constructor(private readonly turret: Turret, mesh: SkinnedMesh, animations: AnimationClip[]) {
        this.animationMixer = new AnimationMixer(mesh);
        this.parseAnimations(animations);
        Ticker.shared.add(this.update, this);
    }

    private parseAnimations(animations: AnimationClip[]): void {
        this.spawn = AnimationParsingUtils.animationActionByName(this.animationMixer, animations, "TurretAppearance");
        if (this.spawn == null) {
            throw new Error("Turret has no spawn animations configured. Check model you importing.");
        }
        this.hide = AnimationParsingUtils.animationActionByName(this.animationMixer, animations, "TurretDisappearance");
        if (this.hide == null) {
            throw new Error("Turret has no hide animations configured. Check model you importing.");
        }
        this.hide.timeScale = 0.5;
        this.shoot = AnimationParsingUtils.animationActionByName(this.animationMixer, animations, "TurretShot");
        if (this.shoot == null) {
            throw new Error("Turret has no shoot animations configured. Check model you importing.");
        }
    }

    private update(dt: number): void {
        this.animationMixer.update(dt / 50);
    }

    public playSpawn(): Promise<void> {
        return new Promise(resolve => {
            const onSpawnEnd = () => {
                this.animationMixer.removeEventListener("finished", onSpawnEnd);
                resolve();
            }
            this.animationMixer.addEventListener("finished", onSpawnEnd);
            this.spawn.reset().play();
            this.animationMixer.setTime(0.1);
            this.turret.turretObjectVisible = true;
        });
    }

    public playShoot(): void {
        this.shoot.reset().play();
    }

    public playHide(): Promise<void> {
        return new Promise(resolve => {
            const onHideEnd = (eventData: { action: AnimationAction, direction: number }) => {
                if (eventData.action.getClip().name === "TurretDisappearance") {
                    this.animationMixer.removeEventListener("finished", onHideEnd);
                    resolve();
                }
            }
            this.animationMixer.addEventListener("finished", onHideEnd);
            this.animationMixer.stopAllAction();
            this.hide.reset().play();
        });
    }

    public destroy(): void {
        Ticker.shared.remove(this.update, this);
    }
}